import * as React from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { selectContactLocations } from '@user/store/map'
import {
  createStaticMarker,
  getVariantFromEntityType,
} from '@user/components/HereMap/utils/createMarker'
import {
  currentLocationSvg,
  CL_ANCHOR_X,
  CL_ANCHOR_Y,
} from '@user/components/HereMap/utils/pinSvg'
import { EntityLocationDTO } from '@user/store/map/typings'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

interface UseHereMapMarkersParams {
  map: H.Map | null
  onMarkerClick: (markerData: EntityLocationDTO) => void
  onMarkerHover: (markerData: EntityLocationDTO | null) => void
  activePin: EntityLocationDTO | null
  contactPinsGroup: H.map.Group | null
  knockStatusGroup: H.map.Group | null
  currentLocationGroup: H.map.Group | null
}

export const useHereMapMarkers = ({
  map,
  onMarkerClick,
  onMarkerHover,
  activePin,
  contactPinsGroup,
  knockStatusGroup,
  // currentLocationGroup: reserved for future use
}: UseHereMapMarkersParams) => {
  const isD2dEnabled = useD2DKnockEnabled()
  const contactLocations = useSelector(selectContactLocations)
  const watchIdRef = React.useRef<number | null>(null)

  const activePinRef = useRef<EntityLocationDTO | null>(activePin)
  const prevActivePinIdRef = useRef<string | null>(activePin?.pinId ?? null)
  activePinRef.current = activePin

  const getKey = useCallback((item?: EntityLocationDTO) => {
    if (!item) return 'unknown'
    const baseKey =
      item.pinId ||
      `${item.entityType ?? 'unknown'}-${item.entityId ?? 'unknown'}-${item.serviceLocationId ?? 'unknown'}`

    const knockColor = item.knockStatusSettingColor ?? 'none'
    const knockIcon = item.knockStatusSettingIcon ?? 'none'
    const lat = item.latitude?.toFixed(5) ?? '0'
    const lng = item.longitude?.toFixed(5) ?? '0'

    return `${baseKey}
    -${lat}-${lng}
    -c${item.count}
    -m${item.multitenant ? 1 : 0}
    -kc${knockColor}
    -ki${knockIcon}`
  }, [])

  const cleanupMarker = useCallback(
    (marker: any) => {
      const data = marker.getData?.()
      data?.__cleanup?.()
      data?.clearEventListener?.()
      const group: H.map.Group | undefined = data?.group
      try {
        if (group) {
          group.removeObject(marker)
        } else {
          map?.removeObject(marker)
        }
      } catch (_e) {
        // already removed — safe to ignore
      }
    },
    [map],
  )

  /**
   * Add Markers Based on contactLocations.
   * activePin is intentionally NOT a dependency — its updates are handled
   * by the dedicated effect below, which flips `selected` imperatively on
   * the affected markers without invalidating this reconcile pass.
   */
  useEffect(() => {
    if (!map) return
    if (typeof window === 'undefined' || !(window as any).H) return
    if (!contactPinsGroup || !knockStatusGroup) return

    const H = (window as any).H as typeof window.H
    const incomingKeys = new Set(contactLocations.map((d) => getKey(d)))
    const currentMarkersMap = new Map<string, any>()

    const groupObjects = [
      ...contactPinsGroup.getObjects(),
      ...knockStatusGroup.getObjects(),
    ]
    groupObjects.forEach((marker: any) => {
      const { contactLocation } = marker.getData?.() || {}
      if (!contactLocation || contactLocation.entityType === 'currentLocation')
        return
      currentMarkersMap.set(getKey(contactLocation), marker)
    })

    currentMarkersMap.forEach((marker, key) => {
      if (!incomingKeys.has(key)) {
        cleanupMarker(marker)
        currentMarkersMap.delete(key)
      }
    })

    contactLocations.forEach((location: EntityLocationDTO) => {
      const key = getKey(location)
      const variant = getVariantFromEntityType(location.entityType)

      if (currentMarkersMap.has(key)) return

      const { multitenant, count } = location
      if (
        (!multitenant && count > 1) ||
        variant === MapPinTypeVariant.CurrentLocation
      )
        return

      const isActive = activePinRef.current?.pinId === location.pinId
      const marker = createStaticMarker(
        H,
        location,
        variant,
        isActive,
        isD2dEnabled,
      )

      const targetGroup =
        location.entityId === 0 ? knockStatusGroup : contactPinsGroup

      const onTap = (evt: Event) => {
        evt.stopPropagation()
        onMarkerClick(location)
      }
      const onEnter = (evt: Event) => {
        evt.stopPropagation()
        onMarkerHover(location)
      }
      const onLeave = (evt: Event) => {
        evt.stopPropagation()
        onMarkerHover(null)
      }

      marker.addEventListener('tap', onTap)
      marker.addEventListener('pointerenter', onEnter)
      marker.addEventListener('pointerleave', onLeave)

      marker.setData({
        ...marker.getData(),
        group: targetGroup,
        clearEventListener: () => {
          marker.removeEventListener('tap', onTap)
          marker.removeEventListener('pointerenter', onEnter)
          marker.removeEventListener('pointerleave', onLeave)
        },
      })

      targetGroup.addObject(marker)
      currentMarkersMap.set(key, marker)
    })
  }, [
    map,
    contactPinsGroup,
    knockStatusGroup,
    contactLocations,
    onMarkerClick,
    onMarkerHover,
    cleanupMarker,
    getKey,
    isD2dEnabled,
  ])

  /**
   * Sync active pin selection without rerunning the markers reconcile.
   * Flips `selected` on the previously-active and newly-active markers via
   * the imperative setSelected exposed on each marker's data.
   */
  useEffect(() => {
    if (!map) return

    const prevPinId = prevActivePinIdRef.current
    const nextPinId = activePin?.pinId ?? null

    if (prevPinId === nextPinId) return

    let prevDeactivated = prevPinId === null
    let nextActivated = nextPinId === null

    const objects: any[] = [
      ...(contactPinsGroup?.getObjects() ?? []),
      ...(knockStatusGroup?.getObjects() ?? []),
    ]

    for (const obj of objects) {
      if (prevDeactivated && nextActivated) break
      const data = (obj as any).getData?.()
      const pinId = data?.contactLocation?.pinId
      if (!pinId) continue
      if (!prevDeactivated && pinId === prevPinId) {
        data.setSelected?.(false)
        prevDeactivated = true
      }
      if (!nextActivated && pinId === nextPinId) {
        data.setSelected?.(true)
        nextActivated = true
      }
    }

    prevActivePinIdRef.current = nextPinId
  }, [map, activePin, contactPinsGroup, knockStatusGroup])

  /**
   * Add Current Location Marker.
   * Uses H.map.DomIcon with an inline SVG string so the pulse animation works
   * in the DOM (static H.map.Icon textures don't support SVG animations).
   */
  useEffect(() => {
    if (!map || !('geolocation' in navigator)) return

    const H = (window as any).H as typeof window.H

    const removeExisting = () => {
      map.getObjects().forEach((obj: any) => {
        if (typeof obj.getData === 'function') {
          const data = obj.getData()
          if (
            data?.contactLocation?.entityType === 'currentLocation' ||
            data?.isAccuracyCircle === true
          ) {
            data?.__cleanup?.()
            map.removeObject(obj)
          }
        }
      })
    }

    const createOrUpdate = (lat: number, lng: number) => {
      removeExisting()

      const contactLocation: EntityLocationDTO = {
        count: 0,
        entityId: 0,
        entityType: 'currentLocation',
        latitude: lat,
        longitude: lng,
        multitenant: false,
        serviceLocationId: 0,
        pinId: 'CURRENT_LOCATION',
        entities: [],
      }

      const domIcon = new H.map.DomIcon(currentLocationSvg(), {
        anchor: { x: CL_ANCHOR_X, y: CL_ANCHOR_Y },
      })
      const domMarker = new H.map.DomMarker({ lat, lng }, { icon: domIcon })
      domMarker.setData({ contactLocation, __cleanup: () => {} })
      map.addObject(domMarker)
    }

    const onSuccess = (pos: GeolocationPosition) => {
      const { latitude, longitude } = pos.coords
      createOrUpdate(latitude, longitude)
    }

    const onError = (error: GeolocationPositionError) => {
      console.error('Geolocation watchPosition error:', error)
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      onSuccess,
      onError,
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 20000,
      },
    )

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }
      removeExisting()
    }
  }, [map])
}
